import * as Model from '../models';
import * as Common from './common';
import { API_ENDPOINTS, AppConfigService } from './app-config-service';
import { AlertService } from './alert-service';

export type AddToCartRestRequest = {
  oligoRequestArray: Common.OligRequestParam[];
};

export type AddToCartRestResponse = {
  cartKey: string;
  errorStrings: string[];
};

export type AddToCartGetPayloadResponse = {
  encrypted: string;
};

export type AddToCartRequest = {
  projects: Model.ProjectItem[];
};

export type AddToCartResponse = {
  cartKey: string;
  cartURL: string;
  errors: string[];
};

export type CartErrorResponse = {
  timestamp: number;
  status: number;
  error: string;
  exception: string;
  message: string;
};

export interface CartService {
  addToCart(request: AddToCartRequest): ng.IPromise<AddToCartResponse>;
}

function projectToCartRequests(allCartReqParams: Common.OligRequestParam[], project: Model.ProjectItem): Common.OligRequestParam[] {
  const cartRequests: Common.OligRequestParam[] = project.oligos.map((oligo: Model.OligoItem): Common.OligRequestParam => {
    const { parameter } = oligo;
    const { basic, advanced } = parameter;
    const {
      oligoName,
      oligoValue,
      researcherName,
      scale,
      purification,
      format,
      prime3Mod,
      prime5Mod,
      delivery,
    } = basic;
    const { alternate, handling } = advanced;
    return {
      oligoName,
      oligoValue,
      researcherName,
      scale,
      purification,
      format,
      prime3Mod,
      prime5Mod,
      delivery,
      alternate,
      handling,
    };
  });
  return [ ...allCartReqParams, ...cartRequests ];
}

type AddToCartProvider = (configService: AppConfigService, alertService: AlertService, $http: ng.IHttpService, deferred: ng.IDeferred<AddToCartResponse>) => AddToCartFn;

type AddToCartFn = (request: AddToCartRestRequest) => ng.IPromise<AddToCartResponse>;

function extractErrors(responseDocument: Document): string[] {
  const cartErrorMessages: string[] = [];
  const cartErrorElements = responseDocument.getElementsByTagName('Error');
  try {
    const cartErrorCount: number = cartErrorElements.length;
    for (let cartErrorIndex: number = 0; cartErrorIndex < cartErrorCount; cartErrorIndex++) {
      const cartErrorElement = cartErrorElements.item(cartErrorIndex);
      if (cartErrorElement) {
        const cartErrorMessageElements = cartErrorElement.getElementsByTagName('Message');
        const cartErrorMessageCount: number = cartErrorMessages.length;
        for (let cartErrorMsgIndex: number = 0; cartErrorMsgIndex < cartErrorMessageCount; cartErrorMsgIndex++) {
          const cartErrorMessageElement = cartErrorMessageElements.item(cartErrorMsgIndex);
          if (cartErrorMessageElement) {
            cartErrorMessages.push(cartErrorMessageElement.textContent || '');
          }
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
  return cartErrorMessages;
}

function isCartSessionRoutingError(contentType: string, data: string): boolean {
  try {
    if (contentType && contentType.indexOf('json')) {
      const cartResponseError: CartErrorResponse = JSON.parse(data);
      return cartResponseError.message ? cartResponseError.message.indexOf('LtCommerceRedirectionFilter') >= 0 : false;
    }
  } catch (e) {
    return false;
  }
  return false;
}

function addToCartWithService(configService: AppConfigService,
                              alertService: AlertService,
                              $http: ng.IHttpService,
                              deferred: ng.IDeferred<AddToCartResponse>): AddToCartFn {
  const path: string = configService.pathFor(API_ENDPOINTS.ADD_TO_CART);
  const cartURL: string = configService.pathFor(API_ENDPOINTS.VIEW_CART);
  return (cartRequest: AddToCartRestRequest) => {
    $http.post<AddToCartRestResponse>(path, cartRequest)
      .then((addToCartResponse: ng.IHttpResponse<AddToCartRestResponse>) => {
        const { data, status, statusText } = addToCartResponse;
        if (status === 200) {
          const { cartKey, errorStrings: errors } = data;
          let dataAsString: string = '';
          try {
            dataAsString = JSON.stringify(data);
          } catch (e) {
            console.error(e);
          }
          alertService.info(`<b>Cart Response Data</b>: ${dataAsString}`).finally(() => {
            deferred.resolve({ cartKey, cartURL, errors });
          });
        } else {
          alertService.error(`<b>Cart Response Status</b>: ${statusText}<br/><b>Cart Response Data</b>: ${data}`);
          deferred.reject(addToCartResponse);
        }
      }, (addToCartErrorResponse: ng.IHttpResponse<string>) => {
        const { data, statusText } = addToCartErrorResponse;
        alertService.error(`<b>Cart Response Status</b>: ${statusText}<br/><b>Cart Response Data</b>: ${data}`);
        deferred.reject(addToCartErrorResponse);
      });
    return deferred.promise;
  };
}

function addToCartWithClient(configService: AppConfigService,
                             alertService: AlertService,
                             $http: ng.IHttpService,
                             deferred: ng.IDeferred<AddToCartResponse>): AddToCartFn {
  const getCartPayloadPath: string = configService.pathFor(API_ENDPOINTS.GET_CART_PAYLOAD);
  const addToCartPath: string = configService.pathFor(API_ENDPOINTS.ADD_TO_CART_COMMERCE);
  const cartLocation: string = configService.pathFor(API_ENDPOINTS.VIEW_CART);
  type LTCommerceData = {
    RequestXML: string;
  };
  const unknownError: string = 'Unknown Error';
  return (cartRequest: AddToCartRestRequest) => {
    $http.post<AddToCartGetPayloadResponse>(getCartPayloadPath, cartRequest)
      .then((cartPayloadResponse: ng.IHttpResponse<AddToCartGetPayloadResponse>) => {
        const { status: cartPayloadResponseStatus, data, statusText: cartPayloadResponseStatusText } = cartPayloadResponse;
        if (cartPayloadResponseStatus === 200) {
          const { encrypted } = data;
          const cartRequestData: LTCommerceData = {
            RequestXML: encrypted,
          };
          $http.post<string>(addToCartPath, cartRequestData, {
            responseType: 'text',
            transformRequest: (requestObj: object) => {
              const requestContentArray: string[] = [];
              for (const key in requestObj) {
                if (requestObj[key]) {
                  requestContentArray.push(`${key}=${requestObj[key]}`);
                }
              }
              return requestContentArray.join('&');
            },
            transformResponse: (responseObj: object) => {
              return responseObj.toString();
            },
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }).then((addToCartResponse: ng.IHttpResponse<string>) => {
            const { data: cartResponseData, headers, status: cartResponseStatus, statusText } = addToCartResponse;
            const contentType: string = headers('Content-Type');
            if (cartResponseStatus === 200) {
              const cartURL: string = headers('location') || cartLocation;
              // if this is an html, this is probably a result
              // of redirection by login due to session expiry, in which case, we reject
              // with an authentication error to cause a redirect later on
              const isHTMLContent: boolean = contentType ? contentType.indexOf('html') >= 0 : false;
              if (isHTMLContent) {
                alertService.error(`<b>Cart Response Status</b>: Redirect due to session expiry<br/><b>Cart Request Payload</b>: ${encrypted}`);
                const sessionExpiredError: ng.IHttpResponse<string> = new Error('401') as object as ng.IHttpResponse<string>;
                sessionExpiredError.status = 401;
                deferred.reject(sessionExpiredError);
              } else {
                try {
                  const responseDocument: Document = new DOMParser().parseFromString(cartResponseData, 'application/xml');
                  const cartKeyElements = responseDocument.getElementsByTagName('CartKey');
                  if (cartKeyElements.length > 0) {
                    const cartErrorMessages: string[] = extractErrors(responseDocument);
                    const cartKeyElement = cartKeyElements.item(0) as Element;
                    const cartKey: string = cartKeyElement.textContent || '';
                    alertService.info(`<b>Cart Response Data</b>: ${cartResponseData}`).finally(() => {
                      deferred.resolve({ cartKey, cartURL, errors: cartErrorMessages });
                    });
                  } else {
                    alertService.error(`<b>Cart Response Data</b>: ${cartResponseData}<br/><b>Cart Response Status</b>: No Cart Key<br/><b>Cart Request Payload</b>: ${encrypted}`);
                    deferred.reject(new Error('No cart key!'));
                  }
                } catch (e) {
                  console.error(e);
                  alertService.error(`<b>Cart Response Data</b>: ${cartResponseData}<br/><b>Cart Failure Reason</b>: ${e}<br/><b>Cart Request Payload</b>: ${encrypted}`);
                  deferred.reject(e || unknownError);
                }
              }
            } else {
              alertService.error(`<b>Cart Failure Reason</b>: ${cartResponseData}<br/><b>Cart Response Status</b>: ${statusText}<br/><b>Cart Request Payload</b>: ${encrypted}`);
              if (isCartSessionRoutingError(contentType, cartResponseData)) {
                const sessionExpiredError: ng.IHttpResponse<string> = new Error('401') as object as ng.IHttpResponse<string>;
                sessionExpiredError.status = 401;
                deferred.reject(sessionExpiredError);
              } else {
                deferred.reject(addToCartResponse);
              }
            }
          }, (addToCartErrorResponse: ng.IHttpResponse<string>) => {
            const { statusText, data: cartResponseData, headers } = addToCartErrorResponse;
            const contentType: string = headers('Content-Type');
            alertService.error(`<b>Cart Failure Reason</b>: ${cartResponseData}<br/><b>Cart Response Status</b>: ${statusText}<br/><b>Cart Request Payload</b>: ${encrypted}`);
            if (isCartSessionRoutingError(contentType, cartResponseData)) {
              const sessionExpiredError: ng.IHttpResponse<string> = new Error('401') as object as ng.IHttpResponse<string>;
              sessionExpiredError.status = 401;
              deferred.reject(sessionExpiredError);
            } else {
              deferred.reject(addToCartErrorResponse);
            }
          });
        } else {
          alertService.error(`<b>Cart Payload Generation Failure Reason</b>: ${data}<br/><b>Cart Payload Generation Status</b>: ${cartPayloadResponseStatusText}`);
          deferred.reject(cartPayloadResponse);
        }
      }, (cartPayloadErrorResponse: ng.IHttpResponse<string>) => {
        const { data, statusText } = cartPayloadErrorResponse;
        alertService.error(`<b>Cart Payload Generation Failure Reason</b>: ${data}<br/><b>Cart Payload Generation Status</b>: ${statusText}`);
        deferred.reject(cartPayloadErrorResponse);
      });
    return deferred.promise;
  };
}

class CartServiceImpl implements CartService {

  constructor(private $q: ng.IQService,
              private $http: ng.IHttpService,
              private configService: AppConfigService,
              private alertService: AlertService) {
  }

  public addToCart(request: AddToCartRequest): ng.IPromise<AddToCartResponse> {
    const { configService, alertService, $http } = this;
    const deferred: ng.IDeferred<AddToCartResponse> = this.$q.defer();
    const { projects } = request;
    const oligoCartRequests: Common.OligRequestParam[] = projects.reduce(projectToCartRequests, []);
    const cartRequest: AddToCartRestRequest = { oligoRequestArray: oligoCartRequests };
    const worldPromise: ng.IPromise<Model.WorldData> = configService.getAppContent('')
      .catch(() => ({ ...Model.WORLD_INITIAL }));
    return worldPromise.then((worldData: Model.WorldData) => {
      const { clientConfig } = worldData;
      const { addToCartMode } = clientConfig;
      const addToCartProvider: AddToCartProvider = addToCartMode === Model.AppCartModes.CLIENT ? addToCartWithClient : addToCartWithService;
      return addToCartProvider(configService, alertService, $http, deferred)(cartRequest);
    });
  }

}

export const CART_SERVICE_NAME = 'cartService';
export const CartServiceFactory = ($q: ng.IQService,
                                   $http: ng.IHttpService,
                                   configService: AppConfigService,
                                   alertService: AlertService): CartService => {
  return new CartServiceImpl($q, $http, configService, alertService);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/service/cart-service.ts